import { useState } from 'react';
import { adminApi, ApiError } from '../lib/api';
import type { BundleKind } from '../../shared/types';
import Eyebrow from './Eyebrow';
import Button from './Button';
import ConfirmModal from './ConfirmModal';

/** Right-column panel on the bundle editor page — the live link plus the
 *  actions that operate on the saved bundle (rotate, send the ready email,
 *  archive, delete). Only rendered once the bundle has a slug (i.e. it's
 *  been saved at least once); AdminBundleEditor shows a placeholder card
 *  in create mode instead. */
export default function BundleLinkPanel({
  bundleId,
  slug,
  kind,
  archivedAt,
  readyEmailedAt,
  dirty,
  onSlugRotated,
  onArchivedChanged,
  onReadyEmailSent,
  onDeleted,
}: {
  bundleId: string;
  slug: string;
  kind: BundleKind;
  archivedAt: number | null;
  readyEmailedAt: number | null;
  /** True when the editor form has unsaved changes — gates "Send ready
   *  email" so it can never act on stale server state relative to what's
   *  showing on screen (e.g. a just-typed, not-yet-saved client email). */
  dirty: boolean;
  onSlugRotated: (slug: string) => void;
  onArchivedChanged: (archivedAt: number | null) => void;
  onReadyEmailSent: (at: number) => void;
  onDeleted: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [rotateConfirming, setRotateConfirming] = useState(false);
  const [rotateError, setRotateError] = useState<string | null>(null);
  const [sendBusy, setSendBusy] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendConfirming, setSendConfirming] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [deleteConfirming, setDeleteConfirming] = useState(false);

  const url = `${window.location.origin}/b/${slug}`;

  async function copyLink() {
    await navigator.clipboard.writeText(url).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  async function handleRotate() {
    setRotating(true);
    setRotateError(null);
    try {
      const res = await adminApi.rotateBundleSlug(bundleId);
      onSlugRotated(res.slug);
      setRotateConfirming(false);
    } catch (err) {
      setRotateError(err instanceof ApiError ? err.message : 'Rotate failed. Try again.');
    } finally {
      setRotating(false);
    }
  }

  async function handleSendEmail(force: boolean) {
    setSendBusy(true);
    setSendError(null);
    try {
      await adminApi.sendBundleReadyEmail(bundleId, { force });
      onReadyEmailSent(Date.now());
      setSendConfirming(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setSendConfirming(true);
        // Sync the displayed "last sent" date to what the server actually
        // has while the re-send confirm is showing, rather than leaving the
        // stale pre-409 value on screen.
        const body = err.body as { ready_emailed_at?: number } | undefined;
        if (body?.ready_emailed_at) onReadyEmailSent(body.ready_emailed_at);
      } else if (err instanceof ApiError) {
        const code = (err.body as { error?: string } | undefined)?.error;
        setSendError(sendErrorMessage(code, err.message));
      } else {
        setSendError('Send failed.');
      }
    } finally {
      setSendBusy(false);
    }
  }

  async function handleArchiveToggle() {
    setArchiveBusy(true);
    setArchiveError(null);
    try {
      const nextArchived = archivedAt === null;
      await adminApi.archiveBundle(bundleId, nextArchived);
      onArchivedChanged(nextArchived ? Date.now() : null);
    } catch (err) {
      setArchiveError(err instanceof ApiError ? err.message : 'Failed to update this link. Try again.');
    } finally {
      setArchiveBusy(false);
    }
  }

  async function confirmDelete() {
    await adminApi.deleteBundle(bundleId);
    onDeleted();
  }

  return (
    <div className="bg-card border border-line rounded-md p-5 sticky top-24 space-y-1">
      <Eyebrow>// Bundle link</Eyebrow>
      <div className="flex items-center gap-2 mt-3 mb-4">
        <span className="flex-1 min-w-0 font-mono text-[12px] text-fg bg-elevated border border-line rounded px-3 py-2 truncate">
          {url}
        </span>
        <Button variant="secondary" size="sm" onClick={copyLink}>
          {copied ? '✓ Copied' : 'Copy'}
        </Button>
      </div>

      <LinkAction label="Rotate link" hint="Old link stops working immediately.">
        {rotateConfirming ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setRotateConfirming(false)}
              disabled={rotating}
              className="text-[12px] font-medium text-muted hover:text-fg px-2 py-1"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleRotate}
              disabled={rotating}
              className="text-[12px] font-medium text-accent hover:bg-accent/10 px-2 py-1 rounded-sm"
            >
              {rotating ? 'Rotating…' : 'Yes, rotate'}
            </button>
          </div>
        ) : (
          <Button variant="secondary" size="sm" onClick={() => setRotateConfirming(true)}>
            Rotate
          </Button>
        )}
      </LinkAction>
      {rotateError && <p className="text-accent text-[12px] pb-2">{rotateError}</p>}

      {kind === 'shoot' ? (
        <div className="py-3 border-t border-line">
          <p className="text-[12px] text-faint">Ready emails are for review bundles.</p>
        </div>
      ) : (
        <>
          <LinkAction
            label="Send ready email"
            hint={
              dirty
                ? 'Save your changes first'
                : readyEmailedAt
                  ? `Sent ${formatShortDate(readyEmailedAt)}`
                  : 'Not sent yet'
            }
          >
            {sendConfirming ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSendConfirming(false)}
                  disabled={sendBusy}
                  className="text-[12px] font-medium text-muted hover:text-fg px-2 py-1"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleSendEmail(true)}
                  disabled={sendBusy || dirty}
                  className="text-[12px] font-medium text-accent hover:bg-accent/10 px-2 py-1 rounded-sm disabled:opacity-40"
                >
                  {sendBusy ? 'Sending…' : 'Re-send anyway'}
                </button>
              </div>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleSendEmail(false)}
                disabled={sendBusy || dirty}
              >
                {sendBusy ? 'Sending…' : readyEmailedAt ? 'Re-send' : 'Send'}
              </Button>
            )}
          </LinkAction>
          {sendError && <p className="text-accent text-[12px] pb-2">{sendError}</p>}
        </>
      )}

      <LinkAction label={archivedAt ? 'Unarchive bundle' : 'Archive bundle'} hint={null}>
        <Button variant="ghost" size="sm" onClick={handleArchiveToggle} disabled={archiveBusy}>
          {archiveBusy ? 'Working…' : archivedAt ? 'Unarchive' : 'Archive'}
        </Button>
      </LinkAction>
      {archiveError && <p className="text-accent text-[12px] pb-2">{archiveError}</p>}

      <LinkAction label="Delete bundle" hint={null}>
        <Button variant="ghost" size="sm" onClick={() => setDeleteConfirming(true)}>
          Delete
        </Button>
      </LinkAction>

      {deleteConfirming && (
        <ConfirmModal
          eyebrow="Delete link"
          title="Delete this link forever?"
          body={
            <p>
              This removes the link and its item list. The scripts inside it
              are never touched — only the bundle. This can't be undone.
            </p>
          }
          confirmLabel="Delete forever"
          busyLabel="Deleting…"
          onCancel={() => setDeleteConfirming(false)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}

function LinkAction({
  label,
  hint,
  children,
}: {
  label: string;
  hint: string | null;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-3 border-t border-line first:border-t-0 first:pt-0">
      <div className="min-w-0">
        <div className="text-[13px] font-semibold text-fg">{label}</div>
        {hint && <div className="text-[11px] text-faint mt-0.5">{hint}</div>}
      </div>
      <div className="flex-none">{children}</div>
    </div>
  );
}

function sendErrorMessage(code: string | undefined, fallback: string): string {
  switch (code) {
    case 'no_client_email':
      return 'Add a client email above to send this.';
    case 'email_not_configured':
      return "Email isn't configured for this deployment.";
    case 'no_ready_scripts':
      return 'No scripts are ready to send yet.';
    case 'send_failed':
      return "Couldn't send — try again.";
    default:
      return fallback;
  }
}

function formatShortDate(ts: number): string {
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
